import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminSidebar from './components/admin/AdminSidebar';
import Login from './components/Login';
import AdminHome from './components/admin/AdminHome';
import SimulationCases from './components/admin/SimulationCases';
import CaseCreationChat from './components/admin/CaseCreationChat';
import CaseManage from './components/admin/CaseManage';
import Classes from './components/admin/Classes';
import ClassDetail from './components/admin/ClassDetail';
import Students from './components/admin/Students';
import StudentDetail from './components/admin/StudentDetail';
import Teachers from './components/admin/Teachers';
import Admins from './components/admin/Admins';
import Profile from './components/admin/Profile';
import './App.css';
import { CasesProvider } from './context/CasesContext';
import { ClassesProvider } from './context/ClassesContext';
import { AacnProvider } from './context/AacnContext';

function App() {
  // Read the persisted login synchronously so the first render already knows the
  // user is logged in. Restoring it in an effect let the route guards redirect to
  // /login (and then /) before the effect ran, so a refresh always landed on home.
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === 'true');
  const [username, setUsername] = useState(() => localStorage.getItem('username') || '');
  const [firstName, setFirstName] = useState(() => localStorage.getItem('firstName') || '');
  const [lastName, setLastName] = useState(() => localStorage.getItem('lastName') || '');
  const [role, setRole] = useState(() => localStorage.getItem('role') || '');

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('role');
    setIsLoggedIn(false);
    setUsername('');
    setFirstName('');
    setLastName('');
    setRole('');
  };

  // Teachers can add/manage simulation cases, and can view (but not edit) Classes/Students.
  // Account management (Teachers page, student login credentials) is admin-only.
  const isTeacher = role === 'teacher';
  const canManageAccounts = isLoggedIn && !isTeacher;

  return (
    <CasesProvider>
    <ClassesProvider>
    <AacnProvider>
    <BrowserRouter>
      <div className="app">
        {isLoggedIn && <AdminSidebar username={username} firstName={firstName} lastName={lastName} isTeacher={isTeacher} onLogout={handleLogout} />}
        <div className="app-body">
          <Routes>
            <Route
              path="/login"
              element={
                isLoggedIn ? <Navigate to="/" replace /> : <Login setIsLoggedIn={setIsLoggedIn} setUsername={setUsername} setFirstName={setFirstName} setLastName={setLastName} setRole={setRole} />
              }
            />
            <Route
              path="/"
              element={isLoggedIn ? <AdminHome /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/cases"
              element={isLoggedIn ? <SimulationCases /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/cases/chat"
              element={isLoggedIn ? <CaseCreationChat /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/cases/:caseId"
              element={isLoggedIn ? <CaseManage isTeacher={isTeacher} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/classes"
              element={isLoggedIn ? <Classes isTeacher={isTeacher} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/classes/:classId"
              element={isLoggedIn ? <ClassDetail isTeacher={isTeacher} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/students"
              element={isLoggedIn ? <Students isTeacher={isTeacher} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/students/:studentId"
              element={isLoggedIn ? <StudentDetail /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/admin/teachers"
              element={canManageAccounts ? <Teachers /> : <Navigate to={isLoggedIn ? '/' : '/login'} replace />}
            />
            <Route
              path="/admin/admins"
              element={canManageAccounts ? <Admins /> : <Navigate to={isLoggedIn ? '/' : '/login'} replace />}
            />
            <Route
              path="/admin/profile"
              element={isLoggedIn ? (
                <Profile
                  username={username}
                  firstName={firstName}
                  lastName={lastName}
                  role={role}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                />
              ) : <Navigate to="/login" replace />}
            />
            <Route
              path="*"
              element={isLoggedIn ? <Navigate to="/" replace /> : <Navigate to="/login" replace />}
            />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
    </AacnProvider>
    </ClassesProvider>
    </CasesProvider>
  );
}

export default App;
